import type { AxiosInstance } from 'axios'
import type { H3Event } from 'h3'
import type { ComputedRef } from 'vue'
import type { NuxtApp } from '#app'
import type { MegaMenuNormalized, MegaMenuPayload } from '~/utils/megaMenu'
import {
  cloneMegaMenu,
  DEFAULT_MEGA_MENU_FALLBACK_FILE,
  EMPTY_MEGA_MENU,
  mergeFullMegaMenu,
} from '~/utils/megaMenu'
import { loadFallbackJsonPayload } from '~/utils/helpers'
import { CachedPageUtils } from '~/utils/CachedPageUtils'

export type UseMegaMenuNavDataReturn = {
  megaMenu: ComputedRef<MegaMenuNormalized>
}

/**
 * @param {AxiosInstance} api - Client HTTP API.
 * @param {string} fallbackFile - Fichier ou URL fallback (`NUXT_PUBLIC_MEGA_MENU_FALLBACK_URL`).
 * @param {string} requestOrigin - Origin SSR / client pour le fetch static.
 * @param {H3Event | undefined} requestEvent - Requête en cours côté serveur, absente dans le navigateur.
 * @returns {Promise<MegaMenuNormalized>} Menu fusionné JSON + API.
 */
async function fetchMegaMenuMerged(
  api: AxiosInstance,
  fallbackFile: string,
  requestOrigin: string,
  requestEvent: H3Event | undefined,
): Promise<MegaMenuNormalized> {
  const jsonPayload: MegaMenuPayload | null = await loadFallbackJsonPayload<MegaMenuPayload>(
    fallbackFile,
    DEFAULT_MEGA_MENU_FALLBACK_FILE,
    requestOrigin,
  )

  let apiPayload: MegaMenuPayload | null = null
  try {
    const { data } = await api.get<MegaMenuPayload>('/api/mega-menu', { timeout: 8000 })
    apiPayload = data ?? null
  } catch (error: unknown) {
    CachedPageUtils.reportFailedApiCall('/api/mega-menu', error, requestEvent)
    apiPayload = null
  }

  const fromJson = mergeFullMegaMenu(cloneMegaMenu(EMPTY_MEGA_MENU), jsonPayload)
  return mergeFullMegaMenu(fromJson, apiPayload)
}

/**
 * Données du mega menu — JSON fallback (`public/`) puis patch API dans le même handler.
 * @returns {UseMegaMenuNavDataReturn} `megaMenu` calculé depuis JSON + patch API.
 */
export function useMegaMenuNavData(): UseMegaMenuNavDataReturn {
  const config: ReturnType<typeof useRuntimeConfig> = useRuntimeConfig()
  const api: AxiosInstance = useFrontApi()
  const megaMenuFallbackFile: string = config.public.megaMenuFallbackFile
  const requestOrigin: string = useRequestURL().origin
  const requestEvent: H3Event | undefined = useRequestEvent()

  const { data: megaMenuMerged } = useAsyncData<MegaMenuNormalized>(
    'mega-menu-nav',
    (): Promise<MegaMenuNormalized> =>
      fetchMegaMenuMerged(api, megaMenuFallbackFile, requestOrigin, requestEvent),
    {
      server: true,
      lazy: true,
      default: () => cloneMegaMenu(EMPTY_MEGA_MENU),
      getCachedData(key: string, nuxtApp: NuxtApp): MegaMenuNormalized | undefined {
        const cached: MegaMenuNormalized | undefined = nuxtApp.payload.data[key] as
          | MegaMenuNormalized
          | undefined
        if (
          import.meta.client &&
          cached &&
          cached.popularCities?.length === 0 &&
          cached.jobSectors?.length === 0
        ) {
          return undefined
        }
        return cached
      },
    },
  )

  const megaMenu: ComputedRef<MegaMenuNormalized> = computed<MegaMenuNormalized>(
    (): MegaMenuNormalized => megaMenuMerged.value ?? cloneMegaMenu(EMPTY_MEGA_MENU),
  )

  return { megaMenu }
}
